import { queryClient } from '@/utils/query/queryClient';
import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';

/**
 * Warms the `['library-sections']` cache — the same query `PresetFeedScreen`
 * reads through `usePresetFeedData` (and the library screen through
 * `useLibrarySectionsQuery`) — so the data is in flight before either screen
 * mounts.
 *
 * Called from `App`'s `useOpen` callback, which runs inside the host's
 * `onOpen` handler — after `setState(true)` but before that re-render commits.
 * So the request is in flight before `AppContent` mounts and before
 * `useAppBootstrap` → `useLibrary` → `useLibrarySectionsQuery` subscribes to
 * this same query, and it isn't held up by the blocking-deeplink `isPending`
 * gate either.
 *
 * Two earlier call sites, both worse. The entry's `setup()` (right after
 * `setupContext`) fires soonest, but the context existing is not the same as
 * the host having answered: `getHeaders()` is still `{}` that early, so
 * `isPortfolioApp()` reads false and `buildLibraryUrl` picks the filtered CMS
 * base instead of the `/portfolio` one — cached for the whole `staleTime`.
 * `AppContent`'s mount effect is correct on that count but gives up a full
 * render, and lands in the same commit as the subscription it was
 * meant to precede. `onOpen` is the earliest point that is still after
 * bootstrap, so the base resolves correctly with no gate needed inside
 * `fetchLibraryData` — this call site is what makes that safe. Moving it any
 * earlier reintroduces the wrong-base bug silently.
 *
 * Must not run before `setupContext`: at module evaluation `fetchLibraryData`
 * swallows the missing-context error and caches `null` as fresh data (empty
 * library for the whole `staleTime`). Safe to call more than once — the host
 * may fire `onOpen` again, and React Query dedupes in-flight fetches and
 * respects `staleTime`.
 */
const prefetchPresetFeedData = (): void => {
  queryClient.prefetchQuery(librarySectionsQueryOptions).catch(() => {
    // prefetch failures are non-fatal — the consuming screen refetches
  });
};

export default prefetchPresetFeedData;
